import React from "react";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import Modal from "./Modal";
import supabase from "../../utils/supabase";
import useUserStore from "../../stores/user";

interface DistrictOption {
  id: string;
  name: string;
}

interface AddSchoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  districtId?: string;
  districtName?: string;
  districts?: DistrictOption[];
  title?: string;
  onSuccess?: (districtId: string) => void;
}

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("School name is required")
    .min(2, "School name must be at least 2 characters"),
  districtId: yup.string().when("districts", {
    is: (districts: any) => Array.isArray(districts) && districts.length > 0,
    then: (schema) => schema.required("District is required"),
    otherwise: (schema) => schema,
  }),
});

export default function AddSchoolModal({
  isOpen,
  onClose,
  districtId,
  districtName,
  districts = [],
  title,
  onSuccess,
}: AddSchoolModalProps) {
  const user = useUserStore((state) => state.user);

  const initialDistrictId =
    districtId || (districts.length > 0 ? districts[0].id : "");

  const handleSubmit = async (
    values: { name: string; districtId?: string },
    { setSubmitting, resetForm }: any
  ) => {
    if (!user) {
      toast.error("User not authenticated");
      setSubmitting(false);
      return;
    }
    const usedDistrictId = districtId || values.districtId;
    if (!usedDistrictId) {
      toast.error("District is required");
      setSubmitting(false);
      return;
    }
    try {
      const { error } = await supabase.from("schools").insert([
        {
          name: values.name,
          user_id: user.id,
          district_id: usedDistrictId,
        },
      ]);

      if (error) {
        console.error("Error adding school:", error);
        toast.error("Failed to add school");
        return;
      }

      toast.success("School added successfully!");
      resetForm();
      onClose();
      onSuccess?.(usedDistrictId);
    } catch (error) {
      console.error("Error adding school:", error);
      toast.error("Failed to add school");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        title ||
        (districtName ? `Add New School to ${districtName}` : "Add New School")
      }
    >
      <Formik
        initialValues={{ name: "", districtId: initialDistrictId }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => (
          <Form className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                School Name
              </label>
              <Field
                name="name"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter school name"
              />
              {formik.errors.name && formik.touched.name && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.name}
                </div>
              )}
            </div>
            {districts.length > 0 && !districtId && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  District
                </label>
                <Field
                  as="select"
                  name="districtId"
                  className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Field>
                {formik.errors.districtId && formik.touched.districtId && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.districtId}
                  </div>
                )}
              </div>
            )}
            <div className="pt-4 flex justify-end space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-small btn-secondary"
                disabled={formik.isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-small ${
                  formik.isSubmitting ? "btn-disabled" : ""
                }`}
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {formik.isSubmitting ? "Adding..." : "Add School"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
